import { Button as HeroButton } from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isKioskLocation } from '../lib/kitchenKiosk';
import { cx, Drawer } from './Ui';

type NavItem = readonly [href: string, label: string, icon: string];

function NavIcon({ icon }: { icon: string }) {
  const common = 'h-5 w-5';
  switch (icon) {
    case '⌂':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      );
    case '☷':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <rect x="4" y="4" width="7" height="7" rx="1.5" />
          <rect x="13" y="4" width="7" height="7" rx="1.5" />
          <rect x="4" y="13" width="7" height="7" rx="1.5" />
          <rect x="13" y="13" width="7" height="7" rx="1.5" />
        </svg>
      );
    case '⚙':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M12 8.5A3.5 3.5 0 1 0 12 15.5A3.5 3.5 0 1 0 12 8.5z" />
          <path d="M19.4 15a1 1 0 0 0 .2 1.1l.1.1a2 2 0 0 1 0 2.8 2 2 0 0 1-2.8 0l-.1-.1a1 1 0 0 0-1.1-.2 1 1 0 0 0-.6.9V20a2 2 0 0 1-4 0v-.2a1 1 0 0 0-.6-.9 1 1 0 0 0-1.1.2l-.1.1a2 2 0 0 1-2.8 0 2 2 0 0 1 0-2.8l.1-.1a1 1 0 0 0 .2-1.1 1 1 0 0 0-.9-.6H4a2 2 0 0 1 0-4h.2a1 1 0 0 0 .9-.6 1 1 0 0 0-.2-1.1l-.1-.1a2 2 0 0 1 0-2.8 2 2 0 0 1 2.8 0l.1.1a1 1 0 0 0 1.1.2 1 1 0 0 0 .6-.9V4a2 2 0 0 1 4 0v.2a1 1 0 0 0 .6.9 1 1 0 0 0 1.1-.2l.1-.1a2 2 0 0 1 2.8 0 2 2 0 0 1 0 2.8l-.1.1a1 1 0 0 0-.2 1.1 1 1 0 0 0 .9.6h.2a2 2 0 0 1 0 4H20a1 1 0 0 0-.9.6Z" />
        </svg>
      );
    case '⌖':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M12 21s6-4.35 6-10a6 6 0 1 0-12 0c0 5.65 6 10 6 10Z" />
          <path d="M12 8.5A2.5 2.5 0 1 0 12 13.5A2.5 2.5 0 1 0 12 8.5z" />
        </svg>
      );
    case '♙':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );
    case '▣':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <path d="M8 9h8M8 13h8M8 17h5" />
        </svg>
      );
    case '♨':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M7 14h10" />
          <path d="M5 17h14" />
          <path d="M9 5c2 1.5 2 3.5 0 5" />
          <path d="M13 3c2 1.5 2 4 0 6" />
          <path d="M17 5c2 1.5 2 3.5 0 5" />
        </svg>
      );
    case '⇶':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <path d="M3 12h11" />
          <path d="m11 8 4 4-4 4" />
          <path d="M17 7h1a3 3 0 0 1 3 3v4" />
          <circle cx="7" cy="18" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="17" cy="18" r="1.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case '▦':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={common}>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <path d="M4 11h16M10 5v14M14 5v14" />
        </svg>
      );
    default:
      return <span className="text-base">{icon}</span>;
  }
}

const navByRole: Record<'owner' | 'manager' | 'reception' | 'kitchen' | 'delivery' | 'waiter', readonly NavItem[]> = {
  owner: [
    ['/dashboard', 'Resumen', '⌂'],
    ['/dashboard/intelligence', 'Inteligencia', '◎'],
    ['/dashboard/torre-control', 'Torre de Control', '⚑'],
    ['/dashboard/menu', 'Menú', '☷'],
    ['/dashboard/inventario', 'Inventario', '▤'],
    ['/dashboard/configuracion', 'Configuración', '⚙'],
    ['/dashboard/branches', 'Sucursales', '⌖'],
    ['/dashboard/users', 'Usuarios', '♙'],
    ['/dashboard/ai-alertas', 'Alertas IA', '⚠'],
    ['/dashboard/reception', 'Recepción', '▣'],
    ['/dashboard/waiter', 'Meseros', '▦'],
    ['/dashboard/kitchen', 'Cocina', '♨'],
    ['/dashboard/delivery', 'Reparto', '⇶'],
  ],
  manager: [
    ['/dashboard', 'Resumen', '⌂'],
    ['/dashboard/intelligence', 'Inteligencia', '◎'],
    ['/dashboard/torre-control', 'Torre de Control', '⚑'],
    ['/dashboard/menu', 'Menú', '☷'],
    ['/dashboard/inventario', 'Inventario', '▤'],
    ['/dashboard/configuracion', 'Configuración', '⚙'],
    ['/dashboard/branches', 'Sucursales', '⌖'],
    ['/dashboard/users', 'Usuarios', '♙'],
    ['/dashboard/ai-alertas', 'Alertas IA', '⚠'],
    ['/dashboard/reception', 'Recepción', '▣'],
    ['/dashboard/waiter', 'Meseros', '▦'],
    ['/dashboard/kitchen', 'Cocina', '♨'],
    ['/dashboard/delivery', 'Reparto', '⇶'],
  ],
  reception: [['/dashboard/reception', 'Recepción', '▣'], ['/dashboard/ai-alertas', 'Alertas IA', '⚠']],
  kitchen: [['/dashboard/kitchen', 'Cocina', '♨']],
  delivery: [['/dashboard/delivery', 'Reparto', '⇶']],
  waiter: [['/dashboard/waiter', 'Meseros', '▦']],
};

function DashboardNavItems({ nav, onNavigate }: { nav: readonly NavItem[]; onNavigate?: () => void }) {
  return (
    <div className="grid gap-[10px]">
      {nav.map(([href, label, icon]) => (
        <NavLink
          key={href}
          to={href}
          end={href === '/dashboard'}
          onClick={onNavigate}
          className={({ isActive }) => cx(
            'dashboard-sidebar-link dashboard-admin-sans group flex items-center gap-[12px] px-[14px] py-[12px] text-[12px] font-semibold tracking-[0.01em] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-black',
            isActive ? 'is-active' : '',
          )}
        >
          <span className="dashboard-sidebar-link-icon grid h-[38px] w-[38px] shrink-0 place-items-center text-current transition">
            <NavIcon icon={icon} />
          </span>
          <span className="min-w-0 truncate">{label}</span>
        </NavLink>
      ))}
    </div>
  );
}

function DashboardSidebarShell({
  currentLabel,
  userName,
  userMeta,
  nav,
  onLogout,
  onNavigate,
  onClose,
  mobile,
}: {
  currentLabel: string;
  userName: string;
  userMeta: string;
  nav: readonly NavItem[];
  onLogout: () => void;
  onNavigate?: () => void;
  onClose?: () => void;
  mobile?: boolean;
}) {
  return (
    <div className="dashboard-admin-sans flex h-full flex-col bg-[var(--app-brand-ink)] text-white">
      <div className="border-b border-white/10 px-5 py-5 shadow-[0_1px_0_rgba(255,255,255,0.05)]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <NavLink to="/dashboard" onClick={onNavigate} className="block text-sm font-black uppercase tracking-[0.24em] text-white">
              Estoy Pidiendo
            </NavLink>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">{currentLabel}</p>
          </div>
          {mobile ? (
            <HeroButton
              isIconOnly
              aria-label="Cerrar navegación"
              variant="ghost"
              className="size-8 shrink-0 rounded-md text-white/60 transition hover:bg-white/8 hover:text-white focus-visible:ring-offset-black"
              onPress={onClose}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </HeroButton>
          ) : null}
        </div>
      </div>

      <div className="border-b border-white/10 bg-white/[0.04] px-5 py-4">
        <div className="dashboard-sidebar-profile border-b border-white/10 px-1 pb-4">
          <div className="truncate text-sm font-bold text-white">{userName}</div>
          <div className="mt-1 truncate text-[11px] uppercase tracking-[0.12em] text-white/48">{userMeta}</div>
        </div>
      </div>

      <nav aria-label="Dashboard navigation" className="sidebar-nav flex-1 overflow-y-auto px-[18px] py-[18px]">
        <DashboardNavItems nav={nav} onNavigate={onNavigate} />
      </nav>

      <div className="border-t border-white/10 p-4">
        <HeroButton
          fullWidth
          type="button"
          variant="ghost"
          onPress={onLogout}
          className="dashboard-sidebar-logout dashboard-admin-sans px-4 py-3 text-[13px] font-semibold text-white transition focus-visible:ring-offset-black"
        >
          Cerrar sesión
        </HeroButton>
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const kiosk = isKioskLocation(location.pathname, searchParams);
  const nav = user ? navByRole[user.role as keyof typeof navByRole] ?? [] : [];
  const currentLabel = useMemo(
    () => nav.find(([href]) => (href === '/dashboard' ? location.pathname === href : location.pathname.startsWith(href)))?.[1] ?? 'Panel',
    [location.pathname, nav],
  );
  const userMeta = user?.email ?? user?.role ?? '';

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const closeMobileMenu = () => setMenuOpen(false);

  // Kitchen kiosk mode (e.g. `/dashboard/kitchen?kiosk=1`) renders the page
  // without the dashboard sidebar/header so a kitchen monitor is dedicated to
  // the queue. Usage is safe: this route is behind ProtectedRoute with roles.
  if (kiosk) return <Outlet />;

  if (!user || user.role === 'super_admin') return null;

  return (
    <div className="app-brand-shell min-h-screen text-[var(--app-brand-ink)] lg:pl-[284px]">
      {menuOpen ? (
        <button
          type="button"
          aria-label="Cerrar navegación"
          className="fixed inset-0 z-40 bg-black/72 backdrop-blur-sm lg:hidden"
          onClick={closeMobileMenu}
        />
      ) : null}

      <header className="dashboard-admin-sans fixed inset-x-0 top-0 z-30 border-b border-white/10 bg-[var(--app-brand-ink)] text-white lg:hidden">
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/45">Dashboard</p>
            <p className="truncate text-sm font-semibold text-white">{currentLabel}</p>
          </div>
          <HeroButton
            aria-expanded={menuOpen}
            aria-controls="dashboard-mobile-nav"
            variant="primary"
            className="border border-accent bg-accent px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-accent-foreground shadow-[0_10px_24px_rgba(255,49,49,0.28)] transition hover:brightness-110 focus-visible:ring-white focus-visible:ring-offset-black"
            onPress={() => setMenuOpen(true)}
          >
            Menú
          </HeroButton>
        </div>
      </header>

      <aside className="fixed inset-y-0 left-0 z-20 hidden w-[284px] border-r border-white/10 bg-[var(--app-brand-ink)] shadow-[0_22px_48px_rgba(26,27,24,0.28)] lg:block">
        <DashboardSidebarShell
          currentLabel={currentLabel}
          userName={user.name}
          userMeta={userMeta}
          nav={nav}
          onLogout={handleLogout}
        />
      </aside>

      <Drawer.Backdrop isOpen={menuOpen} onOpenChange={setMenuOpen} className="lg:hidden">
        <Drawer.Content placement="left" className="w-[88vw] max-w-[320px]">
          <Drawer.Dialog className="border-r border-white/10 bg-[var(--app-brand-ink)]">
            <DashboardSidebarShell
              currentLabel={currentLabel}
              userName={user.name}
              userMeta={userMeta}
              nav={nav}
              onLogout={handleLogout}
              onNavigate={closeMobileMenu}
              onClose={closeMobileMenu}
              mobile
            />
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>

      <main className="min-w-0 px-4 pb-6 pt-20 sm:px-6 sm:pb-8 lg:px-8 lg:pb-10 lg:pt-8">
        <div className="dashboard-admin-sans app-brand-card mb-5 hidden items-center justify-between gap-4 rounded-[1.35rem] px-5 py-4 lg:flex">
          <div className="min-w-0">
            <p className="app-brand-kicker">Dashboard</p>
            <p className="truncate text-sm font-semibold text-[var(--app-brand-ink)]">{currentLabel}</p>
          </div>
          <div className="text-right">
            <div className="truncate text-sm font-semibold text-[var(--app-brand-ink)]">{user.name}</div>
            <div className="truncate text-[11px] uppercase tracking-[0.14em] text-[var(--app-brand-muted)]">{userMeta}</div>
          </div>
        </div>

        <div className="rounded-[1.75rem] bg-[radial-gradient(circle_at_top_right,color-mix(in_oklch,var(--app-brand-red)_8%,transparent),transparent_32rem)]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
